import React, { useState, useRef } from 'react';
import { Row, Col, Typography, Button, Space, Alert, Tooltip } from 'antd';
import { EnvironmentOutlined, ReloadOutlined } from '@ant-design/icons';
import { useAuth } from '../contexts/AuthContext';
import MapCore from '../components/mapping/MapCore';
import SiteSelectionPanel from '../components/mapping/SiteSelectionPanel';
import AdvancedDrawingTools from '../components/mapping/AdvancedDrawingTools';
import HazardOverlayLayer from '../components/mapping/HazardOverlayLayer';
import MapLoadingOverlay from '../components/mapping/MapLoadingOverlay';

const { Title, Text } = Typography;

/**
 * Map Page - Main standalone map view for site management
 * Accessible from sidebar navigation
 */
const Map = () => {
  const { currentUser } = useAuth();
  const [selectedSites, setSelectedSites] = useState([]);
  const [mapKey, setMapKey] = useState(0); // For forcing map refresh
  const [mapInstance, setMapInstance] = useState(null);
  const [allSites, setAllSites] = useState([]);
  
  // Measurement tool state (synced with MapCore)
  const [measurementMode, setMeasurementMode] = useState('idle');
  
  // Loading overlay state - show when map first loads
  const [showLoadingOverlay, setShowLoadingOverlay] = useState(false);
  
  // Track hazard count for smart loading detection
  const [hazardCount, setHazardCount] = useState(0);
  
  // Site icons visibility toggle state
  const [showSiteIcons, setShowSiteIcons] = useState(true);

  // Site color mode toggle state - 'type' or 'status'
  const [siteColorMode, setSiteColorMode] = useState('status');
  
  // Phase C-Alpha: Unit preference state with localStorage persistence
  const [unitPreference, setUnitPreference] = useState(() => {
    try {
      const saved = localStorage.getItem('crisisCompass_unitPreference');
      return saved ? JSON.parse(saved) : 'km';
    } catch (error) {
      console.warn('📏 Failed to load unit preference from localStorage:', error);
      return 'km';
    }
  });

  // Phase C-Alpha: Handle unit preference changes with persistence
  const handleUnitPreferenceChange = (newUnit) => {
    setUnitPreference(newUnit);
    try {
      localStorage.setItem('crisisCompass_unitPreference', JSON.stringify(newUnit));
      console.log('📏 Unit preference saved to localStorage:', newUnit);
    } catch (error) {
      console.warn('📏 Failed to save unit preference to localStorage:', error);
    }
  };

  // Phase 2C-Alpha: Ring styling configuration with localStorage persistence
  const [ringStyleConfig, setRingStyleConfig] = useState(() => {
    try {
      const saved = localStorage.getItem('crisisCompass_ringStyleConfig');
      return saved ? JSON.parse(saved) : {
        colorScheme: 'red',
        customColors: {
          primary: '#ff0000',
          rings: '#ff0000'
        },
        opacity: {
          primary: 0.8,
          rings: 0.4,
          fill: 0.1
        },
        strokeWeight: {
          primary: 2,
          rings: 1
        }
      };
    } catch (error) {
      console.warn('🎨 Failed to load ring style config from localStorage:', error);
      return {
        colorScheme: 'red',
        customColors: {
          primary: '#ff0000',
          rings: '#ff0000'
        },
        opacity: {
          primary: 0.8,
          rings: 0.4,
          fill: 0.1
        },
        strokeWeight: {
          primary: 2,
          rings: 1
        }
      };
    }
  });

  // Phase 2C-Alpha: Handle ring style changes with persistence
  const handleRingStyleChange = (newConfig) => {
    setRingStyleConfig(newConfig);
    try {
      localStorage.setItem('crisisCompass_ringStyleConfig', JSON.stringify(newConfig));
      console.log('🎨 Ring style config saved to localStorage:', newConfig);
    } catch (error) {
      console.warn('🎨 Failed to save ring style config to localStorage:', error);
    }
  };

  // Handle site selection from map
  const handleSitesSelected = (sites) => {
    setSelectedSites(sites);
  };

  // Refresh map
  const handleRefresh = () => {
    setMapKey(prev => prev + 1);
    setSelectedSites([]);
  };

  // Phase C-Alpha: Reference to AdvancedDrawingTools for shape visibility synchronization
  const advancedDrawingToolsRef = useRef(null);

  // Check if user has access
  if (!currentUser?.orgId) {
    return (
      <Alert
        message="Access Denied"
        description="You need to be part of an organisation to access the mapping system."
        type="error"
        showIcon
      />
    );
  }

  return (
    <div className="map-page" style={{ padding: '24px', height: '100vh' }}>
      {/* Page Header */}
      <div className="mb-4">
        <Row justify="space-between" align="middle">
          <Col>
            <Space align="center">
              <EnvironmentOutlined 
                style={{ 
                  fontSize: '24px', 
                  color: '#39325b' 
                }} 
              />
              <div>
                <Title level={2} style={{ margin: 0, color: '#39325b' }}>
                  Organisation Map
                </Title>
                <Text type="secondary">
                  View and manage your organisation's sites with situational awareness
                </Text>
              </div>
            </Space>
          </Col>
          
          <Col>
            <Space>
              <Tooltip title="Refresh the map data and clear all selections">
                <Button 
                  icon={<ReloadOutlined />}
                  onClick={handleRefresh}
                  type="default"
                >
                  Refresh
                </Button>
              </Tooltip>
            </Space>
          </Col>
        </Row>
      </div>

      {/* Main Content */}
      <Row gutter={[24, 24]} style={{ height: 'calc(100% - 140px)' }}>
        {/* Left Sidebar - Drawing Tools */}
        <Col xs={24} lg={6}>
          <div style={{ height: 'calc(100vh - 200px)', overflowY: 'auto' }}>
            <AdvancedDrawingTools
              map={mapInstance}
              onShapeComplete={(bounds, mode) => {
                // Handle shape completion and site selection
                console.log('Shape completed:', bounds, mode);
              }}
              onSelectionChange={(bounds, mode) => {
                // Handle selection changes during editing
                console.log('Selection changed:', bounds, mode);
              }}
              // Measurement tool integration (synced with MapCore)
              measurementMode={measurementMode}
              onStartMeasurement={mapInstance?.measurementFunctionsRef?.current?.startMeasurement}
              onClearMeasurement={mapInstance?.measurementFunctionsRef?.current?.clearMeasurement}
              // Phase 2B-Alpha: Pass measurement functions ref for circle integration
              measurementFunctionsRef={mapInstance?.measurementFunctionsRef}
              // Phase C-Alpha: Pass unit preference props
              unitPreference={unitPreference}
              onUnitPreferenceChange={handleUnitPreferenceChange}
              // Phase 2C-Alpha: Pass ring styling props
              ringStyleConfig={ringStyleConfig}
              onRingStyleChange={handleRingStyleChange}
              // 🎯 PHASE 1: Circle site detection connection
              onCircleComplete={(() => {
                const handler = mapInstance?.measurementFunctionsRef?.current?.handleCircleComplete;
                console.log('🎯 Map.jsx: onCircleComplete handler available:', !!handler);
                return handler;
              })()}
            />
            
            <div style={{ marginTop: 16 }}>
              <HazardOverlayLayer
                map={mapInstance}
                showControls={true}
                onHazardClick={(hazard) => {
                  console.log('Hazard clicked:', hazard);
                }}
                onHazardCountChange={(count) => {
                  console.log('🎯 Map.jsx: Hazard count changed:', count);
                  setHazardCount(count);
                }}
                // Site icons toggle control
                showSiteIcons={showSiteIcons}
                onShowSiteIconsChange={setShowSiteIcons}
                // Site color mode toggle control
                siteColorMode={siteColorMode}
                onSiteColorModeChange={setSiteColorMode}
              />
            </div>
          </div>
        </Col>

        {/* Map Column */}
        <Col xs={24} lg={selectedSites.length > 0 ? 12 : 18}>
          <div style={{ position: 'relative' }}>
            <MapCore
              key={mapKey}
              mapId="main-map"
              height="calc(100vh - 200px)"
              showDrawingTools={true}
              showSiteSelection={true}
              onSitesSelected={handleSitesSelected}
              onMapLoad={(mapInstance) => {
                setMapInstance(mapInstance);
                // Show loading overlay when map loads to cover hazard data loading
                setShowLoadingOverlay(true);
              }}
              onSitesLoaded={(sites) => {
                setAllSites(sites);
              }}
              onMeasurementModeChange={(mode) => {
                console.log('🗺️ Map.jsx: Measurement mode changed to:', mode);
                setMeasurementMode(mode);
              }}
              // Phase C-Alpha: Pass unit preference to MapCore
              unitPreference={unitPreference}
              // Phase 2C-Alpha: Pass ring styling configuration to MapCore
              ringStyleConfig={ringStyleConfig}
              // 🔬 SURGICAL FIX: Disable MapCore drawing manager when AdvancedDrawingTools is active
              useAdvancedDrawingTools={true}
              // Site icons visibility toggle
              showSiteIcons={showSiteIcons}
              // Site color mode toggle
              siteColorMode={siteColorMode}
              className="main-map"
            />
            
            {/* Loading Overlay - Premium UX with smart hazard detection */}
            <MapLoadingOverlay 
              visible={showLoadingOverlay} 
              hazardCount={hazardCount}
            />
          </div>
        </Col>

        {/* Right Sidebar - Site Selection Panel */}
        {selectedSites.length > 0 && (
          <Col xs={24} lg={6}>
            <div style={{ height: 'calc(100vh - 200px)', overflowY: 'auto' }}>
              <SiteSelectionPanel
                selectedSites={selectedSites}
                showMassContact={true}
                title="Selected Sites"
                emptyMessage="Draw a shape on the map to select sites for mass communication."
              />
            </div>
          </Col>
        )}
      </Row>
    </div>
  );
};

export default Map; 